import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { searchMulti, clearSearch } from '../../store/actions/searchActions';
import { detailPath } from '../../utils/paths';
import { regionFromPathname } from '../../utils/regionFromPath';
import { REGION_HREFLANG } from '../../config/publicRegions';
import { useLocation } from 'react-router-dom';
import { Container } from '../../components/styled/Container';
import { PageLayout } from '../../components/styled/PageLayout';
import { useI18n } from '../../hooks/useI18n';
import AdImageSlot from '../../components/ads/AdImageSlot';
import AdInline from '../../components/ads/AdInline';
import { pickStaticAd, cardAds } from '../../data/staticAds';
import { SIMIMOVIES_MANAGED_ADS_ENABLED } from '../../config/ads';
import { useFetchManagedPlacements } from '../../hooks/useFetchManagedPlacements';
import { trackAdClick, trackAdImpression } from '../../store/actions/adActions';
import { selectAdPlacementFirst, selectAdPlacementList } from '../../store/selectors/adSelectors';
import { managedPlacementToAd } from '../../utils/adMapper';
import { makeImpressionKey, shouldTrackImpression } from '../../utils/adTracking';

const SEARCH_AD_REQ = Object.freeze([
  { placement: 'results-inline', limit: 2 },
  { placement: 'inline-card', limit: 1 },
]);

const Title = styled.h1`
  margin: 0 0 1rem;
  font-size: ${({ theme }) => theme.fontClamp['2xl']};
`;

const Form = styled.form`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const Input = styled.input`
  flex: 1;
  min-width: 200px;
  padding: 0.65rem 0.9rem;
  border-radius: ${({ theme }) => theme.radius};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.card};
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontClamp.sm};
`;

const Btn = styled.button`
  padding: 0.65rem 1.1rem;
  border-radius: ${({ theme }) => theme.radius};
  border: none;
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primaryForeground};
  font-weight: 700;
  cursor: pointer;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 1rem;
`;

const Card = styled(Link)`
  text-decoration: none;
  color: inherit;
  border-radius: ${({ theme }) => theme.radius};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadowCard};
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Poster = styled.div`
  aspect-ratio: 2/3;
  background: ${({ theme }) => theme.colors.muted};
  background-image: ${({ $url }) => ($url ? `url(${$url})` : 'none')};
  background-size: cover;
`;

const Cap = styled.div`
  padding: 0.6rem 0.65rem 0.75rem;
  font-size: ${({ theme }) => theme.fontClamp.xs};
  font-weight: 700;
`;

const Badge = styled.span`
  display: block;
  margin-top: 0.25rem;
  font-size: 0.65rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Empty = styled.p`
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const AdStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
  width: 100%;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
`;

const InlineAdWrap = styled.div`
  width: 100%;
  max-width: 640px;
  margin: 0 auto 1.5rem;
`;

export default function SearchPage() {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') || '';
  const [input, setInput] = useState(q);
  const { results, loading, error } = useSelector((s) => s.search);
  const { pathname } = useLocation();
  const region = regionFromPathname(pathname);
  const pre = region || '';
  const serverLanguage = REGION_HREFLANG[region] || 'en-US';
  const { t } = useI18n();

  const strip0Fallback = useMemo(() => pickStaticAd({ position: 'search', size: 'medium', slot: 0 }), []);
  const strip1Fallback = useMemo(() => pickStaticAd({ position: 'search', size: 'medium', slot: 1 }), []);

  const { adDomain, adLocale, adDevice } = useFetchManagedPlacements('search', SEARCH_AD_REQ);
  const managedSearchStrip = useSelector((s) => selectAdPlacementList(s, 'search', 'results-inline'));
  const managedSearchInline = useSelector((s) => selectAdPlacementFirst(s, 'search', 'inline-card'));

  const searchStrip0Ad = useMemo(
    () => managedPlacementToAd(managedSearchStrip[0], strip0Fallback),
    [managedSearchStrip, strip0Fallback],
  );
  const searchStrip1Ad = useMemo(
    () => managedPlacementToAd(managedSearchStrip[1], strip1Fallback),
    [managedSearchStrip, strip1Fallback],
  );
  const searchInlineAd = useMemo(() => managedPlacementToAd(managedSearchInline, cardAds[0]), [managedSearchInline]);

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED) return;
    [managedSearchStrip[0], managedSearchStrip[1]].forEach((camp, idx) => {
      if (!camp?._id) return;
      const key = makeImpressionKey({
        campaign: camp._id,
        page: 'search',
        placement: 'results-inline',
        device: adDevice,
        domain: adDomain,
      });
      if (shouldTrackImpression(key)) {
        dispatch(
          trackAdImpression({
            campaign: camp._id,
            page: 'search',
            placement: 'results-inline',
            device: adDevice,
            domain: adDomain,
            locale: adLocale,
          }),
        );
      }
    });
  }, [dispatch, managedSearchStrip, adDevice, adDomain, adLocale]);

  useEffect(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedSearchInline?._id) return;
    const key = makeImpressionKey({
      campaign: managedSearchInline._id,
      page: 'search',
      placement: 'inline-card',
      device: adDevice,
      domain: adDomain,
    });
    if (shouldTrackImpression(key)) {
      dispatch(
        trackAdImpression({
          campaign: managedSearchInline._id,
          page: 'search',
          placement: 'inline-card',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    }
  }, [dispatch, managedSearchInline, adDevice, adDomain, adLocale]);

  const onSearchStripClick = useCallback(
    (campaignId) => () => {
      if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !campaignId) return;
      dispatch(
        trackAdClick({
          campaign: campaignId,
          page: 'search',
          placement: 'results-inline',
          device: adDevice,
          domain: adDomain,
          locale: adLocale,
        }),
      );
    },
    [dispatch, adDevice, adDomain, adLocale],
  );

  const onSearchInlineClick = useCallback(() => {
    if (!SIMIMOVIES_MANAGED_ADS_ENABLED || !managedSearchInline?._id) return;
    dispatch(
      trackAdClick({
        campaign: managedSearchInline._id,
        page: 'search',
        placement: 'inline-card',
        device: adDevice,
        domain: adDomain,
        locale: adLocale,
      }),
    );
  }, [dispatch, managedSearchInline, adDevice, adDomain, adLocale]);

  useEffect(() => {
    setInput(q);
    if (!q.trim()) {
      dispatch(clearSearch());
      return;
    }
    dispatch(searchMulti(q.trim(), 1, { language: serverLanguage }));
  }, [q, dispatch, serverLanguage]);

  const onSubmit = (e) => {
    e.preventDefault();
    setSearchParams(input.trim() ? { q: input.trim() } : {});
  };

  return (
    <PageLayout>
      <Container>
      <Title>{t('common.search', 'Search')}</Title>
      <Form onSubmit={onSubmit}>
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('search.placeholderShort', 'Movie or TV show...')}
        />
        <Btn type="submit">{t('common.search', 'Search')}</Btn>
      </Form>
      <AdStrip>
        <AdImageSlot
          size="medium"
          position="search"
          slot={0}
          pageKey="search"
          ad={searchStrip0Ad}
          onLinkClick={onSearchStripClick(searchStrip0Ad?.managedCampaignId)}
        />
        <AdImageSlot
          size="medium"
          position="search"
          slot={1}
          pageKey="search"
          ad={searchStrip1Ad}
          onLinkClick={onSearchStripClick(searchStrip1Ad?.managedCampaignId)}
        />
      </AdStrip>
      <InlineAdWrap>
        <AdInline ad={searchInlineAd} pageKey="search" onLinkClick={onSearchInlineClick} />
      </InlineAdWrap>
      {error && <Empty>{error}</Empty>}
      {loading && <Empty>{t('search.searching', 'Searching...')}</Empty>}
      {!loading && q && !results.length && !error && <Empty>{t('search.noResults', 'No results.')}</Empty>}
      <Grid>
        {results.map((r) => {
          if (r.media_type === 'movie') {
            return (
              <Card key={`m-${r.id}`} to={detailPath('movie', r.id, r.title || r.name, pre)}>
                <Poster
                  $url={r.poster_path ? `https://image.tmdb.org/t/p/w342${r.poster_path}` : null}
                />
                <Cap>
                  {r.title || r.name}
                  <Badge>{t('common.movie', 'Movie')} · {t('top.viewDetails', 'open details')}</Badge>
                </Cap>
              </Card>
            );
          }
          if (r.media_type === 'tv') {
            return (
              <Card key={`t-${r.id}`} to={detailPath('tv', r.id, r.name || r.title, pre)}>
                <Poster
                  $url={r.poster_path ? `https://image.tmdb.org/t/p/w342${r.poster_path}` : null}
                />
                <Cap>
                  {r.name || r.title}
                  <Badge>{t('common.tv', 'TV')} · {t('top.viewDetails', 'open details')}</Badge>
                </Cap>
              </Card>
            );
          }
          return null;
        })}
      </Grid>
      </Container>
    </PageLayout>
  );
}
